/**
 * StrokeSetCard — surfaces when the cascade engine flags 'stroke'
 * (stroke / CVA primary impression, suspected-stroke chief complaint,
 * or face-droop / arm-drift / speech findings already in assessment).
 *
 * Captures the LAMS score, last-known-well time, and the three
 * Cincinnati Prehospital Stroke Scale components per RI Protocol 5.04.
 * Feeds NEMSIS, RIEMSIS, the Stroke Center Alert, and ToC handoff.
 */

import type { EPCR } from '@/types';
import { SetCard, LabeledRow, ChipSelect } from './SetCard';
import { FanOutChips } from '@/components/FanOutChips';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

type StrokeFindings = {
  lams_score?: number;
  last_known_well?: string;
  cincinnati_face?: 'normal' | 'droop' | 'unable';
  cincinnati_arm?: 'normal' | 'drift' | 'unable';
  cincinnati_speech?: 'normal' | 'slurred' | 'unable';
};

export function StrokeSetCard({ epcr, onUpdate }: Props) {
  const f = ((epcr.set_findings ?? {}).stroke ?? {}) as StrokeFindings;
  const update = (patch: Partial<StrokeFindings>) => {
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        stroke: { ...f, ...patch },
      },
    });
  };
  const locked = epcr.locked;

  return (
    <SetCard
      title="Stroke / CVA Set"
      triggeredBy="Primary impression or chief complaint suggests stroke / CVA. RI Protocol 5.04 requires LAMS scoring + Cincinnati exam + last-known-well time."
      feeds={['nemsis_xml', 'riemsis_api', 'stroke_alert', 'toc_handoff', 'narrative_engine']}
      accent="rose"
    >
      <LabeledRow label="LAMS Score (0–5)" hint="≥4 → Stroke Center Alert per RI 5.04. Captured once → fans out to alert + NEMSIS eVitals.29.">
        <div className="flex flex-wrap gap-1.5">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              disabled={locked}
              onClick={() => update({ lams_score: n })}
              className={`rounded-md border px-4 py-2 text-base font-bold min-h-touch min-w-touch ${
                f.lams_score === n
                  ? 'bg-rose-600 border-rose-700 text-white'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <FanOutChips capture="stroke.lams_score" />
      </LabeledRow>

      <LabeledRow label="Last Known Well" hint="Last time the patient was at baseline. Drives tPA / thrombectomy eligibility.">
        <input
          type="datetime-local"
          disabled={locked}
          value={f.last_known_well ?? ''}
          onChange={(e) => update({ last_known_well: e.target.value })}
          className="rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
        />
        <FanOutChips capture="stroke.last_known_well" />
      </LabeledRow>

      <LabeledRow label="Cincinnati — Face">
        <ChipSelect
          options={['normal', 'droop', 'unable']}
          value={f.cincinnati_face ?? null}
          disabled={locked}
          onChange={(v) => update({ cincinnati_face: v as StrokeFindings['cincinnati_face'] })}
        />
      </LabeledRow>

      <LabeledRow label="Cincinnati — Arm Drift">
        <ChipSelect
          options={['normal', 'drift', 'unable']}
          value={f.cincinnati_arm ?? null}
          disabled={locked}
          onChange={(v) => update({ cincinnati_arm: v as StrokeFindings['cincinnati_arm'] })}
        />
      </LabeledRow>

      <LabeledRow label="Cincinnati — Speech">
        <ChipSelect
          options={['normal', 'slurred', 'unable']}
          value={f.cincinnati_speech ?? null}
          disabled={locked}
          onChange={(v) => update({ cincinnati_speech: v as StrokeFindings['cincinnati_speech'] })}
        />
        <FanOutChips capture="stroke.cincinnati" />
      </LabeledRow>
    </SetCard>
  );
}
